import type { Company } from "./companies";

/**
 * THE COMPANY A COWORKER IS REGISTERED UNDER — always one of the bearer's own.
 *
 * When the organiser offers coworkers as companions (`FO_MEETING_V2_COMPANIONS_COWORKERS`),
 * the person added by hand (« Ajouter une personne », or « L'ajouter à la main » from the
 * coworkers window) is a NEW coworker: they are attached to a company of the bearer.
 * That is what makes them a coworker, and their badge then names the company. The
 * organiser can ask for the same without the coworkers list
 * (`FO_MEETING_V2_COMPANIONS_COMPANY`).
 *
 *   no company    nothing to attach them to: added as before
 *   one company   attached to it, with no question asked
 *   several       the bearer chooses, and must
 *
 * ⚠️ ONLY THE BEARER'S COMPANIES ARE OFFERED. A participant has no business attaching
 * somebody to a company they do not belong to themselves, and the domain's other
 * companies are not theirs to list.
 *
 * ⚠️ NO DEFAULT AMONG SEVERAL. Pre-ticking the bearer's own company for the event would
 * quietly turn « several » back into « one », the very case the rule keeps apart: with
 * a choice to make, the bearer makes it. What a person ALREADY has is not a default, it
 * is their answer: reopened by « Modifier », the choice starts on it.
 */
export type CompanionCompany = {
  /**
   * The company their participation names for this event (`UserEvents.employeeId`),
   * when it is one of the offered. Choosing it again writes nothing. `null` before the
   * person exists.
   */
  current: number | null;
  /** Where the choice starts: their own answer, else the only possible company. */
  preset: number | null;
};

export function companionCompany({
  offered,
  theirs = [],
  theirEmployeeId,
}: {
  /** The bearer's companies. */
  offered: ReadonlyArray<Company>;
  /** The person's own companies, each with their attachment (`employeeId`). */
  theirs?: ReadonlyArray<Company>;
  /** The attachment their participation to this event names. */
  theirEmployeeId?: number | null;
}): CompanionCompany {
  const isOffered = (id: number) => offered.some((c) => c.id === id);
  const forEvent =
    typeof theirEmployeeId === "number"
      ? theirs.find((c) => c.employeeId === theirEmployeeId)
      : undefined;
  const current = forEvent && isOffered(forEvent.id) ? forEvent.id : null;
  /**
   * ⚠️ A COWORKER CHOSEN FROM THE LIST HAS NO COMPANY FOR THE EVENT YET: choosing them
   * creates their participation without one (`attachCoworker`). The company they share
   * with the bearer is still their answer, when there is only one.
   */
  const shared = theirs.filter((c) => isOffered(c.id));
  const preset =
    current ??
    (shared.length === 1 ? shared[0]!.id : null) ??
    (offered.length === 1 ? offered[0]!.id : null);
  return { current, preset };
}
